// Grids & Gantts — THE RESCHEDULER: a bar moved whole takes the work under
// it along.
//
// A plan is a tree. An order holds assemblies, an assembly holds its
// operations, an operation holds the ones below it and the kits that feed
// it. When the order slides a fortnight, everything under it slides a
// fortnight — and doing that by hand is a few hundred drags, the one that
// gets forgotten being the one that matters. So on a grid whose admin has
// switched it on, for the work types the admin named, dragging a bar WHOLE
// (both its Start date and its Due date, by the same number of days) moves
// every work item below it, at every level, by that same number of days.
//
//   only a whole move   dragging one edge of a bar (only the Start date, or
//                       only the Due date) moves nothing else — that is a
//                       change of duration, not of when the work happens
//   only these types    the admin ticks the work types whose bars carry the
//                       work under them (rescheduleTypes), and for each says
//                       which of the work below stays where it is:
//                         skipDone        work in a Done status category
//                         skipInProgress  work in an In Progress category
//                       A work item left where it is does not shield what is
//                       under it: every item is judged on its own status.
//   only these people   site admins always; members of the groups named in
//                       rescheduleGroups as well. Nobody else gets the
//                       Rescheduler on that grid. OFF by default.
//
// "Under it" is the HIERARCHY the grid itself draws: the folder's Parent
// field and link extenders (e.g. Hierarchy · "Link to Child"), followed to
// EVERY level — not only the levels the folder opens to, and not only the
// rows the reader's filters leave on screen. The server walks it (see
// ../../server/reschedule.js) with this file's arithmetic.
//
// Dates move by CALENDAR days, the same as the bar the hand moved: a work
// item keeps its length and its place relative to its parent. A work item
// with only one of the two dates moves that one; one with neither has
// nothing to move.
//
// PURE and IMPORT-FREE: the server loads this very file (as it loads
// structure.js) so the page and the server cannot disagree about what moves.

const DAY_MS = 86400000;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const toMs = (iso) => Date.parse(`${iso}T00:00:00Z`);
export const isDate = (v) => typeof v === 'string' && DATE.test(v);
export const dayOf = (v) => (v == null || v === '' ? '' : String(v).slice(0, 10));
export const plusDays = (iso, n) => new Date(toMs(iso) + n * DAY_MS).toISOString().slice(0, 10);
export const daysBetween = (a, b) => Math.round((toMs(b) - toMs(a)) / DAY_MS);

const str = (v) => (typeof v === 'string' ? v : (v == null ? '' : String(v)));
const lc = (s) => str(s).trim().toLowerCase();

// ---- the settings ------------------------------------------------------------
// One entry per work type whose bars carry the work under them. Named by
// work type NAME, like every other name in a structure's config.
export function normalizeRescheduleTypes(raw) {
  const out = [];
  const seen = new Set();
  (Array.isArray(raw) ? raw : []).forEach((r) => {
    const o = typeof r === 'string' ? { type: r } : (r && typeof r === 'object' ? r : null);
    const type = o ? str(o.type).trim() : '';
    if (!type || seen.has(lc(type))) return;
    seen.add(lc(type));
    out.push({ type, skipDone: o.skipDone === undefined ? true : !!o.skipDone, skipInProgress: !!o.skipInProgress });
  });
  return out;
}
const names = (raw) => [...new Set((Array.isArray(raw) ? raw : []).map((g) => str(g).trim()).filter(Boolean))];

// The three keys on a structure's config, normalised. `defaults` is the
// host's own defaults (a structure page may pass some); the product's are
// off, no extra groups, no types.
export const RESCHEDULE_KEYS = ['rescheduleOn', 'rescheduleGroups', 'rescheduleTypes'];
export function rescheduleSettingsOf(raw, defaults = {}) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const D = defaults && typeof defaults === 'object' ? defaults : {};
  return {
    rescheduleOn: r.rescheduleOn === undefined ? !!D.rescheduleOn : !!r.rescheduleOn,
    rescheduleGroups: names(r.rescheduleGroups === undefined ? D.rescheduleGroups : r.rescheduleGroups),
    rescheduleTypes: normalizeRescheduleTypes(r.rescheduleTypes === undefined ? D.rescheduleTypes : r.rescheduleTypes),
  };
}

// WHO MAY RESCHEDULE on this grid: a site admin always, otherwise a member of
// one of the named groups. With no group named that is site admins only —
// the default. Nobody at all while the Rescheduler is off.
export function mayReschedule(cfg, userGroups, isAdmin = false) {
  if (!cfg || !cfg.rescheduleOn) return false;
  if (isAdmin) return true;
  const want = names(cfg.rescheduleGroups);
  if (!want.length) return false;
  const mine = new Set((Array.isArray(userGroups) ? userGroups : []).map((g) => str(g)));
  return want.some((g) => mine.has(g));
}

// The admin's rule for a work type, or null when its bars do not carry the
// work under them.
export function rescheduleRuleFor(cfg, typeName) {
  const want = lc(typeName);
  if (!want) return null;
  return normalizeRescheduleTypes(cfg && cfg.rescheduleTypes).find((t) => lc(t.type) === want) || null;
}
export const typeNameOf = (it) => str(it && it.fields && it.fields.issuetype && it.fields.issuetype.name);

// ---- what "under it" means ----------------------------------------------------
// The extenders the walk follows: the dragged row's folder's (its Parent
// field and link rules), or — when the page did not say which folder, or that
// folder is gone — every folder's. At EVERY level: a link rule's level range
// counts from its folder's own root, and the bar being moved is wherever it
// is in that tree, so the range is opened right out.
export function walkRulesOf(cfg, folderId = '') {
  const folders = Array.isArray(cfg && cfg.folders) ? cfg.folders : [];
  const picked = folderId ? folders.filter((f) => f && f.id === folderId) : [];
  const use = picked.length ? picked : folders;
  const links = [];
  const seen = new Set();
  let byParent = false;
  use.forEach((f) => {
    const e = (f && f.expand) || {};
    if (e.byParent === undefined || e.byParent) byParent = true;
    (Array.isArray(e.links) ? e.links : []).forEach((l) => {
      if (!l || !l.relation) return;
      const sig = `${lc(l.type)}|${lc(l.relation)}`;
      if (seen.has(sig)) return;
      seen.add(sig);
      links.push({ type: str(l.type), relation: str(l.relation), levelFrom: 1, levelTo: 999 });
    });
  });
  return { byParent, links };
}

// ---- a whole move ---------------------------------------------------------------
// `from` and `to` are { start, due } as days. A WHOLE move has both dates on
// both sides and shifts them by the same number of days, and not by none.
// Answers that number, or 0 for anything else — one edge dragged, a bar
// with one date only, or nothing moved.
export function wholeMove(from, to) {
  const a = from || {};
  const b = to || {};
  const s0 = dayOf(a.start); const d0 = dayOf(a.due);
  const s1 = dayOf(b.start); const d1 = dayOf(b.due);
  if (!isDate(s0) || !isDate(d0) || !isDate(s1) || !isDate(d1)) return 0;
  const n = daysBetween(s0, s1);
  return n && daysBetween(d0, d1) === n ? n : 0;
}

// One work item's dates after a move of `days`: each date it HAS moves.
export function shiftDates({ start, due } = {}, days) {
  const n = Math.round(Number(days) || 0);
  const s = dayOf(start); const d = dayOf(due);
  return { start: isDate(s) ? plusDays(s, n) : '', due: isDate(d) ? plusDays(d, n) : '' };
}

// The three status categories, the way a person names them.
export function categoryOf(it) {
  const s = it && it.fields && it.fields.status;
  const k = lc(s && s.statusCategory && (s.statusCategory.key || s.statusCategory.name));
  if (k === 'done') return 'done';
  if (k === 'indeterminate' || k === 'in progress' || k === 'inprogress') return 'inProgress';
  return 'todo';
}
export const CATEGORY_LABEL = { done: 'Done', inProgress: 'In Progress', todo: 'To Do' };

// ---- what moves -----------------------------------------------------------------
// The work under `rootKey` (kidsOf: key → child keys; byKey: key → work item),
// each once, with the LEVEL it was first reached at (1 = a child of the root),
// and whether it moves:
//   move: true            it moves by `days`; `to` holds its new dates
//   why: 'done'           left where it is — Done work, and the rule says so
//   why: 'inProgress'     left where it is — In Progress work, and the rule says so
//   why: 'noDates'        nothing to move — it has neither date
// The root itself is not in the list: it is the bar the hand moved.
export function planMoves({ rootKey, byKey, kidsOf, rule, startFieldId, days = 0 }) {
  const r = rule || {};
  const items = [];
  const seen = new Set([rootKey]);
  let frontier = [rootKey];
  let level = 0;
  while (frontier.length) {
    level += 1;
    const next = [];
    frontier.forEach((pk) => (kidsOf.get(pk) || []).forEach((ck) => {
      if (seen.has(ck)) return;
      seen.add(ck);
      const it = byKey.get(ck);
      if (!it) return;
      next.push(ck);
      const f = it.fields || {};
      const start = startFieldId ? dayOf(f[startFieldId]) : '';
      const due = dayOf(f.duedate);
      const cat = categoryOf(it);
      let why = '';
      if (cat === 'done' && r.skipDone) why = 'done';
      else if (cat === 'inProgress' && r.skipInProgress) why = 'inProgress';
      else if (!isDate(start) && !isDate(due)) why = 'noDates';
      const row = {
        key: ck, level, type: typeNameOf(it), summary: str(f.summary), category: cat,
        start: isDate(start) ? start : '', due: isDate(due) ? due : '', move: !why, why,
      };
      if (!why && days) row.to = shiftDates({ start, due }, days);
      items.push(row);
    }));
    frontier = next;
  }
  const levels = items.reduce((m, x) => Math.max(m, x.level), 0);
  const count = (w) => items.filter((x) => x.why === w).length;
  return {
    items, levels,
    counts: { under: items.length, moving: items.filter((x) => x.move).length, done: count('done'), inProgress: count('inProgress'), noDates: count('noDates') },
  };
}

// How many work items sit on each level under the root — "3 levels: 2, 84, 240".
export function perLevel(items) {
  const out = [];
  (items || []).forEach((x) => { out[x.level - 1] = (out[x.level - 1] || 0) + 1; });
  return out.map((n) => n || 0);
}

// ---- the words -------------------------------------------------------------------
// What a work type's rule leaves where it is, in a sentence — for the hover
// card on the bar and the settings both, so they never say different things.
export function scopeSentence(rule) {
  const r = rule || {};
  if (r.skipDone && r.skipInProgress) return 'Work in a Done or an In Progress status stays where it is; work still To Do moves.';
  if (r.skipDone) return 'Work in a Done status stays where it is; everything else moves.';
  if (r.skipInProgress) return 'Work in an In Progress status stays where it is; everything else moves, Done work included.';
  return 'All the work under it moves, whatever its status — Done work included.';
}
export const signedDays = (n) => `${n > 0 ? '+' : (n < 0 ? '−' : '')}${Math.abs(n)} day${Math.abs(n) === 1 ? '' : 's'}`;
